"use client";
import React from "react";
import { cloneDeep } from "lodash";
import {
  Data,
  Filter,
  FilterDispatcherOptions,
  FiltersContextProps,
  FiltersInitialStateProps,
} from "./types";

export const FiltersContext = React.createContext<FiltersContextProps>(undefined);

export const FiltersProvider = ({ children }: { children: React.ReactNode }) => {
  const [filters, setFilters] = React.useState<Filter[]>([]);
  const [data, setData] = React.useState<Data[]>([]);
  const [isLoading, setIsLoading] = React.useState(false);

  const setInitialState = (setInitialState: FiltersInitialStateProps) => {
    const { filters, data } = setInitialState;

    if (filters) {
      setFilters(filters);
    }

    if (data) {
      setData(data);
    }
  };

  const addFilter = (filter: Filter) => {
    setFilters((prevFilters) => [...cloneDeep(prevFilters), filter]);
  };

  const removeAllFilters = () => {
    setFilters([]);
  };

  const removeFilter = (ids: string[] | "all") => {
    if (ids === "all") {
      removeAllFilters();
      return;
    }

    const newFilters = filters.filter((filter) => !ids.includes(filter.id));
    setFilters(newFilters);
  };

  const updateFilter = (id: string, filter: Filter) => {
    const newFilter = filter;

    setFilters((prevFilters) => {
      return prevFilters.map((filter) => {
        if (filter.id === id) {
          return newFilter;
        }
        return filter;
      });
    });
  };

  const getFilter = (id: string): Filter | undefined => {
    return filters.find((filter) => filter.id === id);
  };

  const getFilterIndex = (id: string) => {
    return filters.findIndex((filter) => filter.id === id);
  };

  const getDataIndex = (id: string) => {
    return data.findIndex((data) => data.id === id);
  };

  const getData = (id: string) => {
    return data.find((data) => data.id === id);
  };

  const removeData = (id: string) => {
    const newData = data.filter((data) => data.id !== id);
    setData(newData);
  };

  const updateData = (id: string, newData: Data) => {
    setData((prevData) => {
      return prevData.map((data) => {
        if (data.id === id) {
          return { ...data, ...newData };
        }
      });
    });
  };

  const onLoading = (idData: string, idFilter: string, isLoading = false) => {
    const filterIndex = getFilterIndex(idFilter);
    const dataIndex = getDataIndex(idData);

    const newData = [...data];
    const newFilters = [...filters];

    if (filterIndex >= 0 && dataIndex >= 0) {
      newData[dataIndex].isLoading = isLoading;
      newFilters[filterIndex].isLoading = isLoading;
    }
    setIsLoading(isLoading);
    setData(newData);
  };

  const dispatchFilterRequest = async (id: string, opts?: FilterDispatcherOptions) => {
    const { dataId, request: newRequest } = opts || {};

    const idData = dataId || id;
    const idFilter = id;

    const dataIndex = getDataIndex(idData);
    const filterIndex = getFilterIndex(idFilter);

    onLoading(idData, idFilter, true);
    const requestResponse = newRequest
      ? await newRequest()
      : await filters[filterIndex]?.requestHandler();
    onLoading(idData, idFilter, false);

    const newData = [...data];
    if (dataIndex === -1) {
      newData.push({ id: idData, data: requestResponse?.data });
    } else {
      newData[dataIndex].data = requestResponse?.data;
    }

    setData(newData);
  };

  const resetFilterData = () => {
    setData([]);
  };

  const contextValue: FiltersContextProps = {
    data,
    filters,
    isLoading,
    setInitialState,
    addFilter,
    removeFilter,
    updateFilter,
    getFilter,
    getData,
    updateData,
    removeData,
    dispatchFilterRequest,
    resetFilterData,
  };

  return <FiltersContext.Provider value={contextValue}>{children}</FiltersContext.Provider>;
};
